<template>
  <div class="fiche-vo-landscape-strict">

    <header class="header">
      <div class="col-title">
        <div class="meta-row">
          <span class="badge-occasion">{{ $t('posts.view.pdfVoRefPrefix') }}{{ post.reference }}</span>
        </div>
        <h1 class="titre-vehicule">{{ titreVitrine }}</h1>
      </div>
      <div class="col-price">
        <div class="prix-montant">{{ post.prixDisplay }}</div>
        <div class="prix-mention">{{ $t('posts.view.withoutLicenceAndFuelFees') }}</div>
      </div>
    </header>

    <div class="content-wrapper">

      <div class="main-content">
        <div class="col-left">
          <div class="image-wrapper">
            <img
                v-if="post.photoPrincipale && post.photoPrincipale.url"
                :src="post.photoPrincipale.url"
                :alt="$t('posts.view.pdfVoMainPhotoAlt')"
                class="photo-principale"
            />
            <div v-else class="no-photo-box">
              <ubi-icon name="camera" size="80" style="color: #bdc3c7;"></ubi-icon>
            </div>

            <div v-if="garantieBadgeText" class="badge-garantie">{{ garantieBadgeText }}</div>
          </div>
        </div>

        <div class="col-right">
          <div class="specs-card">
            <div class="spec-col">
              <div class="spec-item">
                <div class="spec-icon">
                  <i class="mdi mdi-calendar"></i>
                </div>
                <div class="spec-text">
                  <div class="spec-lbl">{{ $t('posts.view.pdfVoFirstRegistrationShort') }}</div>
                  <div class="spec-val">{{ post.mise_en_circulation || '-' }}</div>
                </div>
              </div>
              <div class="spec-item">
                <div class="spec-icon">
                  <i class="mdi mdi-speedometer"></i>
                </div>
                <div class="spec-text">
                  <div class="spec-lbl">{{ $t('posts.view.pdfVoMileageLabel') }}</div>
                  <div class="spec-val">{{ post.kilometrage || '-' }} km</div>
                  <div class="spec-sub">{{ post.kilometrage_garanti ? $t('posts.view.pdfVoMileageGuaranteedShort') : $t('posts.view.pdfVoMileageNotGuaranteedShort') }}</div>
                </div>
              </div>
              <div class="spec-item">
                <div class="spec-icon">
                  <i class="mdi mdi-lightning-bolt"></i>
                </div>
                <div class="spec-text">
                  <div class="spec-lbl">{{ $t('posts.view.power') }}</div>
                  <div class="spec-val" v-if="post.puissance_reelle || post.puissance_fiscale">{{ post.puissance_reelle }} {{ $t('posts.view.pdfVoHorsepowerUnit') }}</div>
                  <div class="spec-val" v-else>-</div>
                </div>
              </div>
            </div>
            <div class="spec-col">
              <div class="spec-item">
                <div class="spec-icon">
                  <i class="mdi mdi-gas-station"></i>
                </div>
                <div class="spec-text">
                  <div class="spec-lbl">{{ $t('posts.view.energy') }}</div>
                  <div class="spec-val">{{ post.energie || '-' }}</div>
                  <div class="spec-sub spec-sub-rechargeable" v-if="hybrideRechargeableLabel">{{ hybrideRechargeableLabel }}</div>
                </div>
              </div>
              <div class="spec-item">
                <div class="spec-icon">
                  <i class="mdi mdi-cog"></i>
                </div>
                <div class="spec-text">
                  <div class="spec-lbl">{{ $t('posts.view.pdfVoGearboxShort') }}</div>
                  <div class="spec-val">{{ post.boite_de_vitesse || '-' }}</div>
                </div>
              </div>
              <div class="spec-item">
                <div class="spec-icon">
                  <i class="mdi mdi-leaf"></i>
                </div>
                <div class="spec-text">
                  <div class="spec-lbl">{{ $t('posts.view.vignette') }}</div>
                  <div class="spec-val">{{ $t('posts.view.pdfVoCritairLabel') }} {{ post.classe_critair || '-' }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="contact-card">
            <div class="contact-name">{{ sitimmoUser.raisonSociale }}</div>
            <div class="contact-address">{{ sitimmoUser.adresse }} - {{ sitimmoUser.codePostal }} {{ sitimmoUser.ville }}</div>

            <div class="contact-btn">
              <div class="btn-icon">
                <i class="mdi mdi-phone"></i>
              </div>
              <div class="btn-text">
                <div class="btn-label">{{ $t('posts.view.pdfVoPhoneLabel') }}</div>
                <div class="btn-val">{{ sitimmoUser.telephone }}</div>
              </div>
            </div>

            <div v-if="sitimmoUser.email" class="contact-btn">
              <div class="btn-icon">
                <i class="mdi mdi-email-outline"></i>
              </div>
              <div class="btn-text">
                <div class="btn-label">{{ $t('posts.view.pdfVoEmailLabel') }}</div>
                <div class="btn-val" style="font-size: 11px;">{{ sitimmoUser.email }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div class="equipments-wrapper" v-if="hasEquipmentsSection">
      <div class="equipments-section">
        <h2 class="equipments-title">{{ $t('posts.view.equipmentsAndOptions') }}</h2>

        <div v-if="qualifiedEquipments.length" class="equipments-dynamic-grid" :class="{ 'compact-mode': qualifiedEquipments.length > 24 }">
          <div v-for="(equip, idx) in qualifiedEquipments" :key="'eq-'+idx" class="equip-dynamic-item">
            <span class="equip-check">✓</span>
            <span class="equip-text">{{ equip }}</span>
          </div>
        </div>

        <div v-if="unqualifiedEquipmentsText" class="unqualified-equipments-text">
          <strong>{{ $t('posts.edit.otherEquipements') || 'Autres équipements :' }}</strong> {{ unqualifiedEquipmentsText }}
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import UbiIcon from '@/components/Icon.vue'
import { $t } from '@/i18n'
import { getTitreAlternatif } from '@/helpers/posts/getTitreAlternatif'
import { getPdfVoGarantieBadgeText } from '@/helpers/posts/getPdfVoGarantieBadge'
import { hasHybrideRechargeable, shortHybrideRechargeableLabel } from '@/helpers/posts/hasHybrideRechargeable'

export default {
  name: 'PostPdfVoLandscape',
  components: { UbiIcon },
  props: ['post', 'sitimmoUser', 'universe'],
  methods: {
    normalizeRawEquipments (value) {
      if (value === null || value === undefined) return ''
      const normalized = String(value).trim()
      if (!normalized) return ''
      const lower = normalized.toLowerCase()
      if (lower === 'null' || lower === 'undefined') return ''
      return normalized
    },
    getFormattedUnqualifiedEquipments (maxLength) {
      const raw = this.normalizeRawEquipments(this.post.equipements_non_qualifies)
      if (!raw) return ''

      const items = raw.split(/[,;]/)
        .map(s => s.trim())
        .filter(s => s.length > 0)

      if (!items.length) return ''

      let text = ''
      let isTruncated = false

      for (const item of items) {
        const candidate = text ? `${text}, ${item}` : item
        if (candidate.length > maxLength) {
          isTruncated = true
          break
        }
        text = candidate
      }

      if (!text) {
        text = items[0].slice(0, maxLength)
        isTruncated = true
      }

      if (!isTruncated && text.length < raw.trim().length) {
        isTruncated = true
      }

      return `${text}...`
    }
  },
  computed: {
    titreVitrine () {
      return getTitreAlternatif(this.post)
    },
    qualifiedEquipments () {
      const raw = this.normalizeRawEquipments(this.post.equipments)
      if (!raw) return []
      return raw.split(/[,;]/)
        .map(s => s.trim())
        .filter(s => s.length > 0)
    },
    unqualifiedEquipmentsText () {
      // Augmentee pour exploiter l'espace restant avant saut de page.
      return this.getFormattedUnqualifiedEquipments(1400)
    },
    hasEquipmentsSection () {
      return this.qualifiedEquipments.length > 0 || !!this.unqualifiedEquipmentsText
    },
    garantieBadgeText () {
      return getPdfVoGarantieBadgeText(this.post, {
        monthUnit: this.$t('posts.view.pdfVoWarrantyMonthUnit'),
        prefix: this.$t('posts.view.pdfVoMileageGuaranteedShortTitle')
      })
    },
    hybrideRechargeableLabel () {
      if (!hasHybrideRechargeable(this.post)) return ''
      const labels = this.$labels
      return shortHybrideRechargeableLabel(labels && labels.hybride_rechargeable)
    }
  }
}
</script>

<style scoped>
.fiche-vo-landscape-strict {
  width: 1123px !important;
  min-width: 1123px !important;
  max-width: 1123px !important;
  min-height: 794px !important;
  background-color: #ffffff;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #1b4b53;
  display: block;
  box-sizing: border-box;
  /* Marge horizontale unique : header + corps + équipements alignés */
  padding-left: 40px;
  padding-right: 40px;
  margin: 0 auto;
}

/* MODIFICATION HEADER */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 85px; /* Ajusté pour permettre au trait bleu de passer sans trop d'espace */
  padding: 0;
  box-sizing: border-box;
  margin-bottom: 8px; /* Réduit pour gagner de la place */
}

.col-title {
  width: 65%;
  padding-top: 15px;
  padding-bottom: 10px; /* Juste ce qu'il faut pour aérer le bas du titre */
  box-sizing: border-box;
}

.meta-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.badge-occasion {
  background-color: #1b4b53;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 4px;
  margin-right: 15px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.titre-vehicule {
  font-size: 32px;
  font-weight: 900;
  color: #1b4b53;
  margin: 0;
  line-height: 1.1;
  text-transform: uppercase;
}

.col-price {
  width: 35%;
  background-color: #23b1b6;
  color: #ffffff;
  border-radius: 0 0 15px 15px;
  padding: 15px 20px;
  text-align: center;
  box-sizing: border-box;
}

.prix-montant {
  font-size: 44px;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 5px;
}

.prix-mention {
  font-size: 9px;
  font-weight: bold;
  text-transform: uppercase;
}

.content-wrapper {
  padding: 0;
  width: 100%;
  box-sizing: border-box;
}

.main-content {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  height: 280px;
}

.col-left {
  width: 62%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.image-wrapper {
  position: relative;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f4f6f8;
  padding: 15px;
  box-sizing: border-box;
}

.photo-principale {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border: none !important;
  outline: none !important;
}

.no-photo-box {
  background-color: transparent;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.badge-garantie {
  position: absolute;
  bottom: 15px;
  left: 15px;
  background-color: #1b4b53;
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: bold;
  z-index: 10;
}

.col-right {
  width: 35%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.specs-card {
  height: 145px;
  background-color: #ffffff;
  border: 1px solid #eef1f3;
  border-radius: 12px;
  padding: 12px 15px;
  display: flex;
  box-sizing: border-box;
}

.spec-col {
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.spec-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 5px;
}

.spec-icon {
  margin-right: 10px;
  width: 25px;
  display: flex;
  justify-content: center;
  padding-top: 0;
}

.spec-icon i {
  font-size: 22px;
  color: #23b1b6;
}

.spec-text {
  display: flex;
  flex-direction: column;
}

.spec-lbl {
  height: 12px;
  font-size: 9px;
  color: #8a969b;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 1px;
  display: block;
}

.spec-val {
  height: 15px;
  font-size: 12px;
  font-weight: 900;
  color: #1b4b53;
  display: block;
}

.spec-sub {
  height: 10px;
  font-size: 8px;
  color: #8a969b;
  font-style: italic;
  display: block;
}

.spec-sub-rechargeable {
  font-size: 8px;
  font-weight: 700;
  font-style: italic;
  line-height: 10px;
  height: auto;
  margin-bottom: 2px;
}

.contact-card {
  flex: 1;
  background-color: #1b4b53;
  border-radius: 12px;
  padding: 12px 15px;
  color: #ffffff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.contact-name {
  font-size: 15px;
  font-weight: 900;
  margin-bottom: 2px;
  line-height: 1.1;
  color: #23b1b6;
}

.contact-address {
  font-size: 11px;
  color: #a4b2b6;
  margin-bottom: 5px;
  line-height: 1.3;
}

.contact-btn {
  background-color: #1f606b;
  border-radius: 10px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.btn-icon {
  background-color: #23b1b6;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.btn-icon i {
  font-size: 12px;
}

.btn-text {
  display: flex;
  flex-direction: column;
}

.btn-label {
  font-size: 7px;
  color: #a4b2b6;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.btn-val {
  font-size: 12px;
  font-weight: bold;
  color: #ffffff;
}

.equipments-wrapper {
  padding: 0;
  width: 100%;
  box-sizing: border-box;
}

.equipments-section {
  padding-top: 15px;
}

.equipments-title {
  font-size: 14px;
  font-weight: 900;
  color: #1b4b53;
  margin: 0 0 10px 0;
  text-transform: uppercase;
}

/* MODIFICATION GRILLE 4 COLONNES */
.equipments-dynamic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  column-gap: 15px;
  row-gap: 8px;
  width: 100%;
}

.equip-dynamic-item {
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  page-break-inside: avoid;
  break-inside: avoid;
}

.equip-check {
  color: #23b1b6;
  border: 1px solid #23b1b6;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 8px;
  margin-right: 6px;
  flex-shrink: 0;
  font-weight: bold;
}

.equip-text {
  font-size: 11px;
  font-weight: 600;
  color: #4a5c60;
  line-height: 1.3;
}

.equipments-dynamic-grid.compact-mode {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  column-gap: 10px;
  row-gap: 6px;
}

.equipments-dynamic-grid.compact-mode .equip-text {
  font-size: 9.5px;
  line-height: 1.2;
}

.equipments-dynamic-grid.compact-mode .equip-check {
  width: 11px;
  height: 11px;
  font-size: 7px;
  margin-right: 4px;
}

.unqualified-equipments-text {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #ecf0f1;
  font-size: 10px;
  color: #7f8c8d;
  line-height: 1.5;
  text-align: justify;
}
.unqualified-equipments-text strong {
  color: #1b4b53;
}
</style>
